import {ExternalFindings} from "../../../external-findings";
import {calculatePrevailingRhythm, PrevailingRhythm} from "./calculate-prevailing-rhythm";
import {ConfigField, SentenceSelector} from "./sentence-selector";

export class CardiologsSentenceSelector implements SentenceSelector {

  constructor(
    protected readonly externalFindings: ExternalFindings
  ) {
    this.prevailingRhythmValue = calculatePrevailingRhythm(externalFindings.afibBurden);
  }

  protected readonly prevailingRhythmValue: PrevailingRhythm | undefined;

  selectPrevailingRhythmSentence(): ConfigField[] {
    switch (this.prevailingRhythmValue) {
      case "SR":
        return ["findings_prevailingRhythm_sinus"];
      case "?":
        return ["findings_prevailingRhythm_unknown"];
      case "AF":
        return ["findings_prevailingRhythm_afib"];
      default:
        return [];
    }
  }

  selectAverageHeartRateSentence(): ConfigField[] {
    if (this.prevailingRhythmValue === "SR" || this.prevailingRhythmValue === "AF") {
      return ["findings_averageHeartRate"];
    } else {
      return ["findings_averageHeartRateWhenSinusPlusAfib"];
    }
  }

  selectMinimumHeartRateSentence(): ConfigField[] {
    if (this.prevailingRhythmValue === "SR" || this.prevailingRhythmValue === "AF") {
      return ["findings_minimumHeartRate"];
    }

    if (
      this.externalFindings.minimumHeartRate === this.externalFindings.afibMinimumHeartRate
      || this.externalFindings.minimumHeartRate === this.externalFindings.sinusMinimumHeartRate
    ) {
      return ["findings_minimumHeartRateSinusAndAtrial"];
    }

    return ["findings_minimumHeartRate"];
  }

  selectMaximumHeartRateSentence(): ConfigField[] {
    if (this.prevailingRhythmValue === "SR" || this.prevailingRhythmValue === "AF") {
      if (this.prevailingRhythmValue === "SR" && this.externalFindings.maximumHeartRate !== this.externalFindings.sinusMaximumHeartRate) {
        return ["findings_maximumHeartRate", "findings_maximumHeartRateNotMaximumSinus"];
      }

      return ["findings_maximumHeartRate"];
    }

    if (
      this.externalFindings.maximumHeartRate === this.externalFindings.afibMaximumHeartRate
      || this.externalFindings.maximumHeartRate === this.externalFindings.sinusMaximumHeartRate
    ) {
      return ["findings_maximumHeartRateSinusAndAtrial"];
    }

    return ["findings_maximumHeartRate"];
  }

  selectSentenceForPausesOverThreshold(): ConfigField[] {
    switch (this.externalFindings.pausesOverThreshold) {
      case undefined:
        return [];
      case 0:
        return ["findings_noPausesOverThreshold"];
      case 1:
        return ["findings_onePauseOverThreshold"];
      case 2:
        return ["findings_twoPausesOverThreshold"];
      default:
        return ["findings_pausesOverThreshold"];
    }
  }

  selectSentenceForVentricularBeats(): ConfigField[] {
    switch (this.externalFindings.ventricularBeats) {
      case undefined:
        return [];
      case 0:
        return ["findings_noVentricularBeats"];
      case 1:
        return ["findings_oneVentricularBeat"];
      default:
        return ["findings_ventricularBeats"];
    }
  }

  selectSentenceForVentricularRuns(): ConfigField[] {
    return [];
  }

  selectSentenceForVentricularTachycardias(): ConfigField[] {
    const usedFields: (keyof ExternalFindings)[] = [
      "ventricularBeats",
      "ventricularBeatsBurden",
      "ventricularTachycardias",
      "ventricularTachycardiasLongestDurationBeats",
      "ventricularTachycardiasLongestDuration",
      "ventricularTachycardiasLongestDurationMaxHeartRate",
      "ventricularTachycardiasMaxHeartRate",
      "ventricularTachycardiasMaxHeartRateDuration_deprecated"
    ];
    if (usedFields.some(field => this.externalFindings[field] === undefined)) {
      return [];
    }

    return [
      this.selectFirstVentricularTachycardiasSentence(),
      this.selectSecondVentricularTachycardiasSentence()
    ].filter((field): field is ConfigField => field !== undefined);
  }

  selectSentenceForSlowVentricularRhythms(): ConfigField[] {
    if (this.externalFindings.slowVentricularRhythmCount === undefined
      || this.externalFindings.slowVentricularRhythmLongestDuration === undefined) {
      return [];
    }

    if (this.externalFindings.slowVentricularRhythmCount === 0) {
      return [];
    }

    if (this.externalFindings.slowVentricularRhythmCount === 1) {
      if (this.externalFindings.slowVentricularRhythmLongestDuration <= 10) {
        return ["findings_oneShortSlowVentricularRhythm"];
      } else {
        return ["findings_oneLongerSlowVentricularRhythm"];
      }
    }

    if (this.externalFindings.slowVentricularRhythmCount === 2) {
      if (this.externalFindings.slowVentricularRhythmLongestDuration <= 10) {
        return ["findings_twoShortSlowVentricularRhythm"];
      } else {
        return ["findings_twoLongerSlowVentricularRhythm"];
      }
    }

    if (this.externalFindings.slowVentricularRhythmCount > 2) {
      if (this.externalFindings.slowVentricularRhythmLongestDuration! <= 10) {
        return ["findings_someShortSlowVentricularRhythm"];
      } else {
        return ["findings_someLongerSlowVentricularRhythm"];
      }
    }

    return [];
  }

  selectSentenceForAtrialBeats(): ConfigField[] {
    if (this.prevailingRhythmValue === "AF") {
      return [];
    }

    switch (this.externalFindings.atrialBeats) {
      case undefined:
        return [];
      case 0:
        return ["findings_noAtrialBeats"];
      case 1:
        return ["findings_oneAtrialBeat"];
      default:
        return ["findings_atrialBeats"];
    }
  }

  selectSentenceForSupraventricularRuns(): ConfigField[] {
    return [];
  }

  selectSentenceForAtrialTachycardias(): ConfigField[] {
    if (this.prevailingRhythmValue === "AF") {
      return [];
    }
    return [
      this.selectFirstAtrialTachycardiasSentence(),
      this.selectSecondAtrialTachycardiasSentence()
    ].filter((field): field is ConfigField => field !== undefined);
  }

  selectSentenceForCommentAtrial(): ConfigField[] {
    if (this.externalFindings.afibBurden === 0 && this.externalFindings.atrialTachycardias! > 0) {
      return ["findings_noAtrialFibrillation"];
    }
    return [];
  }

  selectSentenceForCommentVentricular(): ConfigField[] {
    if (this.externalFindings.ventricularCoupletsCount === undefined
      || this.externalFindings.ventricularBeatsBurden === undefined) {
      return [];
    }

    if (this.externalFindings.ventricularBeatsBurden! > 1) {
      if (this.externalFindings.ventricularCoupletsCount === 0) {
        return ["findings_noVentricularCouplets"];
      }

      if (this.externalFindings.ventricularCoupletsCount === 1) {
        return ["findings_oneVentricularCouplet"];
      }

      if (this.externalFindings.ventricularCoupletsCount! > 1) {
        return ["findings_someVentricularCouplets"];
      }
    }
    return [];
  }

  protected selectFirstVentricularTachycardiasSentence(): ConfigField | undefined {
    if (this.externalFindings.ventricularTachycardias === 0 && this.externalFindings.ventricularBeatsBurden! <= 1) {
      return undefined;
    }

    if (this.externalFindings.ventricularTachycardias === 0 && this.externalFindings.ventricularBeatsBurden! > 1) {
      return "findings_noVentricularTachycardias";
    }

    if (this.externalFindings.ventricularTachycardias === 1) {
      if (this.externalFindings.ventricularTachycardiasLongestDuration! <= 10) {
        return "findings_oneVentricularTachycardiaWithLongestDurationLessOrEqualThan10";
      } else {
        return "findings_oneVentricularTachycardiaWithLongestDurationGreaterThan10";
      }
    }

    if (this.externalFindings.ventricularTachycardias === 2) {
      if (this.externalFindings.ventricularTachycardiasLongestDuration! <= 10) {
        return "findings_twoVentricularTachycardiasWithLongestDurationLessOrEqualThan10";
      } else {
        return "findings_twoVentricularTachycardiasWithLongestDurationGreaterThan10";
      }
    }

    if (this.externalFindings.ventricularTachycardias! > 2) {
      if (this.externalFindings.ventricularTachycardiasLongestDuration! <= 10) {
        return "findings_someVentricularTachycardiasWithLongestDurationNotGreaterThan10";
      } else {
        return "findings_someVentricularTachycardiasWithLongestDurationGreaterThan10";
      }
    }

    return undefined;
  }

  protected selectSecondVentricularTachycardiasSentence(): ConfigField | undefined {
    const longestVtIsFastest = this.externalFindings.ventricularTachycardiasMaxHeartRate
      === this.externalFindings.ventricularTachycardiasLongestDurationMaxHeartRate;

    if (this.externalFindings.ventricularTachycardias === 2) {
      if (longestVtIsFastest) {
        return "findings_twoVentricularTachycardiasMaxHrEqualsLongestDurationMaxHr";
      } else {
        if (this.externalFindings.ventricularTachycardiasMaxHeartRateDuration_deprecated! <= 10) {
          return "findings_twoVentricularTachycardiasWithMaxHrLessOrEqual10";
        } else {
          return "findings_twoVentricularTachycardiasWithMaxHrGreaterThan10";
        }
      }
    }

    if (this.externalFindings.ventricularTachycardias! > 2) {
      if (longestVtIsFastest) {
        return "findings_ventricularTachycardiasMaxHrEqualsLongestDurationMaxHr";
      } else {
        if (this.externalFindings.ventricularTachycardiasMaxHeartRateDuration_deprecated! <= 10) {
          return "findings_ventricularTachycardiasMaxHrDurationNotGreaterThan10";
        } else {
          return "findings_ventricularTachycardiasMaxHrDurationGreaterThan10";
        }
      }
    }

    return undefined;
  }

  protected areAnyAtrialTachychardiaFieldsUndefined(): boolean {
    const atrialTachychardiaFields: (keyof ExternalFindings)[] = [
      "afibBurden",
      "atrialTachycardias",
      "atrialTachycardiasLongestDuration",
      "atrialTachycardiasMaximumHeartRate",
      "atrialTachycardiasLongestDurationMaxHeartRate",
      "atrialTachycardiasMaximumHeartRateDuration",
    ];

    return atrialTachychardiaFields.some(field => this.externalFindings[field] === undefined);
  }

  private selectFirstAtrialTachycardiasSentence(): ConfigField | undefined {
    if (this.areAnyAtrialTachychardiaFieldsUndefined()) {
      return undefined;
    }

    switch (this.externalFindings.atrialTachycardias!) {
      case 0:
        return "findings_noAtrialTachycardias";
      case 1:
        if (this.externalFindings.atrialTachycardiasLongestDuration! <= 10) {
          return "findings_oneAtrialTachycardiaWithLongestDurationLessOrEqualThan10";
        } else {
          return "findings_oneAtrialTachycardiaWithLongestDurationGreaterThan10";
        }
      case 2:
        if (this.externalFindings.atrialTachycardiasLongestDuration! <= 10) {
          return "findings_twoAtrialTachycardiaWithLongestDurationLessOrEqualThan10";
        } else {
          return "findings_twoAtrialTachycardiaWithLongestDurationGreaterThan10";
        }
      default: {
        if (this.externalFindings.atrialTachycardiasLongestDuration! <= 10) {
          return "findings_atrialTachycardiasLongestDurationLessOrEqualThan10";
        } else {
          return "findings_atrialTachycardiasLongestDurationGreaterThan10";
        }
      }
    }
  }

  protected selectSecondAtrialTachycardiasSentence(): ConfigField | undefined {
    if (this.areAnyAtrialTachychardiaFieldsUndefined()) {
      return undefined;
    }

    const longestSvtIsFastest = this.externalFindings.atrialTachycardiasMaximumHeartRate
      === this.externalFindings.atrialTachycardiasLongestDurationMaxHeartRate;

    if (this.externalFindings.atrialTachycardias === 2) {
      if (longestSvtIsFastest) {
        return "findings_twoAtrialTachycardiasMaxHrEqualsLongestDurationMaxHr";
      }

      if (this.externalFindings.atrialTachycardiasMaximumHeartRateDuration! <= 10) {
        return "findings_twoAtrialTachycardiasMaxHrDurationNotGreaterThan10";
      } else {
        return "findings_twoAtrialTachycardiasMaxHrDurationGreaterThan10";
      }
    }

    if (this.externalFindings.atrialTachycardias! > 2) {
      if (longestSvtIsFastest) {
        return "findings_atrialTachycardiasMaxHrEqualsLongestDurationMaxHr";
      }

      if (this.externalFindings.atrialTachycardiasMaximumHeartRateDuration! <= 10) {
        return "findings_atrialTachycardiasMaxHrDurationNotGreaterThan10";
      } else {
        return "findings_atrialTachycardiasMaxHrDurationGreaterThan10";
      }
    }

    return undefined;
  }
}
